import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppDispatch } from '../../redux/store';
import { logout } from '../../redux/auth/authSlice';
import Sidebar from '../../components/sidebar/Sidebar';
import TaapTabs from '../../components/taap/TaapTabs';
import { Button } from '../../components/ui/button';
import { Menu } from 'lucide-react';
import metaConnectionService from '../../services/api/metaConnectionService';
import { MetaConnectionState, MetaDashboardData, MetaDatePreset, MetaProposal } from '../../types/metaConnection';
import { reviewSafeText, useMetaLang, MD_STRINGS, toggleLabel, otherLang } from '../../i18n/metaReviewLang';
import {
  ResponsiveAppShell,
  TaskyPageHeader,
  TaskySegmentedControl,
  TaskySelect,
  TaskyStatCard,
} from '../../components/tasky';
import { TaskyHeaderActions } from '../../components/tasky/layout/TaskyHeaderActions';
import MetaAdsWriteSection from '../../components/ad-dashboard/MetaAdsWriteSection';

type MdStrings = (typeof MD_STRINGS)['ja'];

// ============================================================
// Tailwind class constants (旧 styled-components)
// ============================================================

const PAGE_CONTAINER = 'flex min-h-[calc(100dvh_-_56px)] bg-gray-50 lg:h-screen lg:min-h-0';

const MAIN_CONTENT = 'flex-1 overflow-y-auto overflow-x-hidden flex flex-col';

// TaapTabs を出す 5 画面で本体幅を揃える（タブ切替で幅と位置が動かないようにする）。
const CONTENT_AREA = 'p-6 max-w-[1400px] w-full mx-auto max-[640px]:p-4';

const SOURCE_NOTE = "text-[12px] text-gray-500 mb-4 bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 flex items-center gap-2 flex-wrap [&_code]:font-['SFMono-Regular',Menlo,monospace] [&_code]:text-slate-700";

const FILTER_ROW = 'flex items-center justify-between mb-5 flex-wrap gap-3 max-[640px]:items-stretch max-[640px]:flex-col';

const ACCOUNT_SELECT_WRAP = 'w-[240px] max-w-full max-[640px]:w-full';

const KPI_ROW = 'grid grid-cols-3 gap-4 mb-6 max-[900px]:grid-cols-2 max-[640px]:grid-cols-1';

const REFRESH_BUTTON = 'inline-flex items-center gap-1.5 px-4 py-2 text-[14px] font-medium border border-gray-300 rounded-lg bg-white text-gray-700 cursor-pointer hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed';

const SECTION_TITLE = 'text-[16px] font-semibold text-gray-800 m-0 mb-3';

const TABLE_WRAPPER = 'bg-white border border-gray-200 rounded-xl overflow-x-auto mb-7';

const TABLE = 'w-full min-w-[720px] border-collapse';

const TH = 'text-left px-4 py-3 text-[12px] font-semibold text-gray-500 uppercase tracking-wider border-b border-b-gray-200 bg-gray-50 whitespace-nowrap';

const TD = 'px-4 py-3 text-[14px] text-gray-700 border-b border-b-gray-100 whitespace-nowrap';

const BADGE_BASE = 'inline-block px-[10px] py-0.5 rounded-full text-[12px] font-medium';

const PROPOSAL_GRID = 'grid grid-cols-2 gap-4 mb-6 max-[768px]:grid-cols-1';

const PROPOSAL_CARD = 'bg-white border border-gray-200 rounded-xl p-[18px] border-l-4 border-l-primary';

const PROPOSAL_TOP = 'flex items-center justify-between mb-2';

const PROPOSAL_CATEGORY = 'text-[12px] text-primary font-semibold';

const PROPOSAL_TITLE = 'text-[15px] font-bold text-gray-800 mb-1.5';

const PROPOSAL_DESC = 'text-[13px] text-gray-600 leading-[1.6]';

const PROPOSAL_METRIC = 'mt-3 text-[13px] text-gray-500 [&_b]:text-gray-800';

const EMPTY_STATE = 'text-center px-6 py-12 text-gray-400 text-[14px]';

const CONNECT_PROMPT = 'bg-white border border-dashed border-gray-300 rounded-xl px-6 py-12 text-center';

const LOADING = 'p-12 text-center text-gray-400';

// ============================================================
// Helpers
// ============================================================

const DATE_PRESETS: { value: MetaDatePreset; key: 'presetToday' | 'preset7' | 'preset30' | 'presetLifetime' }[] = [
  { value: 'today', key: 'presetToday' },
  { value: 'last_7d', key: 'preset7' },
  { value: 'last_30d', key: 'preset30' },
  { value: 'maximum', key: 'presetLifetime' },
];

const CURRENCY_SYMBOL: Record<string, string> = { JPY: '¥', USD: '$', EUR: '€', GBP: '£' };

function formatMoney(amount: number, currency: string | null): string {
  const sym = currency ? CURRENCY_SYMBOL[currency] : undefined;
  const zeroDecimal = currency === 'JPY' || !currency;
  const formatted = amount.toLocaleString('ja-JP', {
    minimumFractionDigits: zeroDecimal ? 0 : 2,
    maximumFractionDigits: zeroDecimal ? 0 : 2,
  });
  if (sym) return sym + formatted;
  return `${currency ?? ''} ${formatted}`.trim();
}

function formatNumber(n: number): string {
  return n.toLocaleString('ja-JP');
}

const StatusBadge: React.FC<{ status: string; activeLabel: string; pausedLabel: string }> = ({
  status,
  activeLabel,
  pausedLabel,
}) => {
  const up = (status || '').toUpperCase();
  // #16a34a=green-600 / #dcfce7=green-100 / #6b7280=gray-500 / #f3f4f6=gray-100
  // / #dc2626=red-600 / #fee2e2=red-100
  if (up === 'ACTIVE') return <span className={`${BADGE_BASE} text-green-600 bg-green-100`}>{activeLabel}</span>;
  if (up === 'PAUSED') return <span className={`${BADGE_BASE} text-gray-500 bg-gray-100`}>{pausedLabel}</span>;
  return <span className={`${BADGE_BASE} text-red-600 bg-red-100`}>{status || '—'}</span>;
};

// ProposalCard の border-left 色は inline style で上書きする（元実装踏襲）
const SEVERITY_BORDER: Record<MetaProposal['severity'], string> = {
  high: '#dc2626',
  medium: '#d97706',
  low: '#616FC9',
};

// #b91c1c=red-700 / #fee2e2=red-100 / #b45309=amber-700 / #fef3c7=amber-100
// low だけ DS の primary トークンへ寄せる（紫の直書きを残さない）
const SEVERITY_STYLE: Record<MetaProposal['severity'], string> = {
  high: 'text-red-700 bg-red-100',
  medium: 'text-amber-700 bg-amber-100',
  low: 'text-primary bg-primary/10',
};

function severityText(sev: MetaProposal['severity'], t: MdStrings): string {
  return sev === 'high' ? t.sevHigh : sev === 'medium' ? t.sevMedium : t.sevLow;
}

// ============================================================
// Component
// ============================================================

const MetaDashboardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [lang, setLang] = useMetaLang();
  const t = MD_STRINGS[lang];

  const [datePreset, setDatePreset] = useState<MetaDatePreset>('last_30d');
  const [connection, setConnection] = useState<MetaConnectionState | null>(null);
  const accountParam = searchParams.get('account');
  const selectedAccount = connection?.accounts?.find((account) => String(account.id) === accountParam)
    ?? connection?.accounts?.[0];
  const adAccountId = selectedAccount?.platform_account_id;
  const scope = JSON.stringify([adAccountId, datePreset, lang]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const loadId = useRef(0);
  const [loadedData, setLoadedData] = useState<{ scope: string; value: MetaDashboardData } | null>(null);
  const data = loadedData?.scope === scope ? loadedData.value : null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const selectAccount = useCallback((id?: number) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (id) next.set('account', String(id));
      else next.delete('account');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    let active = true;
    metaConnectionService.getConnection().then((next) => {
      if (active) setConnection(next);
    }).catch(() => {
      if (active) {
        setError(MD_STRINGS[lang].errLoad);
        setLoading(false);
      }
    });
    return () => { active = false; };
  }, [lang]);

  // URL uses our account id; the Meta API requires the platform account id.
  // An account from another medium must never be sent to the Meta endpoint.
  useEffect(() => {
    if (connection && accountParam !== (selectedAccount ? String(selectedAccount.id) : null)) {
      selectAccount(selectedAccount?.id);
    }
  }, [connection, accountParam, selectedAccount, selectAccount]);

  const loadData = useCallback(async () => {
    if (!connection) return;
    const requestId = ++loadId.current;
    const isCurrent = () => currentScope.current === scope && requestId === loadId.current;
    setLoading(true);
    setError(null);
    setLoadedData(null);
    try {
      const res = await metaConnectionService.getDashboard({ datePreset, adAccountId, lang });
      if (!isCurrent()) return;
      if (adAccountId && res.selected_account?.platform_account_id !== adAccountId) {
        // A disconnect between the two requests can make the API fall back to
        // another account. Discard those metrics and resolve the selection again.
        const nextConnection = await metaConnectionService.getConnection();
        if (!isCurrent()) return;
        if (!nextConnection.accounts?.some((account) => account.platform_account_id === adAccountId)) {
          setConnection(nextConnection);
        } else {
          // Do not automatically loop if the endpoints still disagree.
          setError(lang === 'en'
            ? 'The selected account could not be loaded. Select another account or try again.'
            : '選択した広告アカウントを読み込めませんでした。別のアカウントを選ぶか、再読み込みしてください。');
        }
        return;
      }
      setLoadedData({ scope, value: res });
    } catch (err) {
      if (!isCurrent()) return;
      const anyErr = err as { response?: { data?: { message?: string } } };
      setError(lang === 'en' ? MD_STRINGS.en.errLoad : anyErr?.response?.data?.message || MD_STRINGS.ja.errLoad);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [connection, datePreset, adAccountId, lang, scope]);

  useEffect(() => {
    loadData();
    return () => { loadId.current += 1; };
  }, [loadData]);

  // Never fall back to a different write account if the dashboard does not
  // report the account requested by this scope.
  const writeAccount = selectedAccount && data?.selected_account?.platform_account_id === adAccountId
    ? {
      id: selectedAccount.id,
      canManage: selectedAccount.can_manage_ads === true,
      writeAllowed: connection?.write_enabled === true && selectedAccount.write_allowed === true,
    }
    : null;

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const kpis = data?.kpis;
  const currency = kpis?.currency ?? null;
  const sidebar = (
    <Sidebar
      onLogout={handleLogout}
      onToggleCollapse={(c) => setSidebarCollapsed(c)}
      collapsed={sidebarCollapsed}
    />
  );
  const mobileSidebar = <Sidebar onLogout={handleLogout} />;

  return (
    <ResponsiveAppShell
      sidebar={sidebar}
      mobileSidebar={mobileSidebar}
      topbarTitle={t.topbarTitle}
      topbarActions={
        <>
          <Button variant="outline" size="sm" onClick={() => setLang(otherLang(lang))} title="Language">
            {toggleLabel(lang)}
          </Button>
        </>
      }
    >
    <div className={PAGE_CONTAINER}>
      <div className={MAIN_CONTENT}>
        <TaskyPageHeader
          className="hidden lg:block"
          title={
            <span className="inline-flex items-center gap-3">
              {sidebarCollapsed && (
                <Button variant="ghost" size="sm" onClick={() => setSidebarCollapsed(false)} className="p-1 h-8 w-8">
                  <Menu className="w-5 h-5" />
                </Button>
              )}
              {t.pageTitle}
            </span>
          }
          actions={
            <>
              <Button variant="outline" size="sm" onClick={() => setLang(otherLang(lang))} title="Language">
                🌐 {toggleLabel(lang)}
              </Button>
              <TaskyHeaderActions />
            </>
          }
        />

        <div className={CONTENT_AREA}>
          <div className="mb-5" data-tutorial="ad-dashboard-tabs"><TaapTabs active="meta" /></div>
          {loading ? (
            <div className={LOADING}>{t.loading}</div>
          ) : error && !data ? null : !data?.connected ? (
            <div className={CONNECT_PROMPT}>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                {t.notConnectedTitle}
              </div>
              <div style={{ color: '#6b7280', marginBottom: 20 }}>
                {t.notConnectedBody}
              </div>
              <Button onClick={() => navigate('/settings/media-connect')}>{t.goToConnect}</Button>
            </div>
          ) : (
            <>
              {(data.fetched_at || (lang === 'en' && data.source)) && (
                <div className={SOURCE_NOTE}>
                  {/* The API call is shown for Meta App Review (English UI); Japanese users only need the time. */}
                  {lang === 'en' && data.source && (
                    <>
                      <span>{t.dataSource}</span>
                      <code>{data.source}</code>
                    </>
                  )}
                  {data.fetched_at && (
                    <span style={{ marginLeft: 'auto' }}>
                      {t.fetched} {new Date(data.fetched_at).toLocaleString(lang === 'en' ? 'en-US' : 'ja-JP')}
                    </span>
                  )}
                </div>
              )}

              <div className={FILTER_ROW} data-tutorial="ad-dashboard-filter">
                <TaskySegmentedControl<MetaDatePreset>
                  aria-label={lang === 'en' ? 'Date range' : '集計期間'}
                  options={DATE_PRESETS.map((p) => ({ value: p.value, label: t[p.key] }))}
                  value={datePreset}
                  onChange={setDatePreset}
                />
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  {(data.accounts?.length ?? 0) > 1 && (
                    <div className={ACCOUNT_SELECT_WRAP}>
                      <TaskySelect
                        value={data.selected_account?.platform_account_id ?? ''}
                        aria-label={lang === 'en' ? 'Ad account' : '広告アカウント'}
                        onChange={(e) => {
                          const account = connection?.accounts?.find((item) => item.platform_account_id === e.target.value);
                          if (account) selectAccount(account.id);
                        }}
                      >
                        {data.accounts.map((a) => (
                          <option key={a.platform_account_id} value={a.platform_account_id}>
                            {reviewSafeText(a.account_name, lang, a.platform_account_id)}
                          </option>
                        ))}
                      </TaskySelect>
                    </div>
                  )}
                  <button className={REFRESH_BUTTON} onClick={loadData} disabled={loading}>
                    ⟳ {loading ? t.refreshing : t.refresh}
                  </button>
                </div>
              </div>

              {/* KPI Cards */}
              <div className={KPI_ROW} data-tutorial="ad-dashboard-kpi">
                <TaskyStatCard label={t.kpiImpressions} value={kpis ? formatNumber(kpis.impressions) : '-'} />
                <TaskyStatCard label={t.kpiClicks} value={kpis ? formatNumber(kpis.clicks) : '-'} />
                <TaskyStatCard label={t.kpiReach} value={kpis ? formatNumber(kpis.reach ?? 0) : '-'} />
                <TaskyStatCard label={t.kpiConversions} value={kpis ? formatNumber(kpis.conversions ?? 0) : '-'} />
                <TaskyStatCard label={t.kpiSpend} value={kpis ? formatMoney(kpis.spend, currency) : '-'} />
                <TaskyStatCard label={t.kpiCtr} value={kpis ? `${kpis.ctr.toFixed(2)}%` : '-'} />
              </div>

              {/* Campaigns Table */}
              <h2 className={SECTION_TITLE}>{t.campaignsSection}</h2>
              <div className={TABLE_WRAPPER} data-tutorial="ad-dashboard-table">
                {data.campaigns.length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table className={TABLE}>
                      <thead>
                        <tr>
                          <th className={TH}>{t.thCampaign}</th>
                          <th className={TH}>{t.thStatus}</th>
                          <th className={TH}>{t.thImpressions}</th>
                          <th className={TH}>{t.thClicks}</th>
                          <th className={TH}>{t.thReach}</th>
                          <th className={TH}>{t.thConversions}</th>
                          <th className={TH}>{t.thCtr}</th>
                          <th className={TH}>{t.thSpend}</th>
                          <th className={TH}>{t.thCpc}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.campaigns.map((c, index) => (
                          <tr key={c.id}>
                            <td className={TD} style={{ fontWeight: 500, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {reviewSafeText(c.name, lang, `Campaign ${index + 1}`)}
                            </td>
                            <td className={TD}>
                              <StatusBadge status={c.status} activeLabel={t.statusActive} pausedLabel={t.statusPaused} />
                            </td>
                            <td className={TD}>{formatNumber(c.impressions)}</td>
                            <td className={TD}>{formatNumber(c.clicks)}</td>
                            <td className={TD}>{formatNumber(c.reach ?? 0)}</td>
                            <td className={TD}>{formatNumber(c.conversions ?? 0)}</td>
                            <td className={TD}>{c.ctr.toFixed(2)}%</td>
                            <td className={TD}>{formatMoney(c.spend, currency)}</td>
                            <td className={TD}>{formatMoney(c.cpc, currency)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className={EMPTY_STATE}>{t.campaignsEmpty}</div>
                )}
              </div>

              {/* AI Proposals */}
              <h2 className={SECTION_TITLE}>{t.proposalsSection}</h2>
              {data.proposals.length > 0 ? (
                <div className={PROPOSAL_GRID}>
                  {data.proposals.map((p) => {
                    const sevTone = SEVERITY_STYLE[p.severity];
                    return (
                      <div className={PROPOSAL_CARD} key={p.id} style={{ borderLeftColor: SEVERITY_BORDER[p.severity] }}>
                        <div className={PROPOSAL_TOP}>
                          <span className={PROPOSAL_CATEGORY}>{p.category}</span>
                          <span className={`${BADGE_BASE} ${sevTone}`}>{severityText(p.severity, t)}</span>
                        </div>
                        <div className={PROPOSAL_TITLE}>
                          {reviewSafeText(p.title, lang, 'Optimization suggestion')}
                        </div>
                        {p.campaign_name && (
                          <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 6 }}>
                            {t.target} {reviewSafeText(p.campaign_name, lang, 'Campaign')}
                          </div>
                        )}
                        <div className={PROPOSAL_DESC}>
                          {reviewSafeText(p.description, lang, 'Optimization suggestion based on live campaign data.')}
                        </div>
                        <div className={PROPOSAL_METRIC}>
                          {reviewSafeText(p.metric.label, lang, 'Metric')}:{' '}
                          <b>{reviewSafeText(p.metric.value, lang, '—')}</b>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={TABLE_WRAPPER}>
                  <div className={EMPTY_STATE}>{t.proposalsEmpty}</div>
                </div>
              )}

              {/* Graph writes (ads_management): plan -> confirm -> apply. */}
              {writeAccount && (
                <MetaAdsWriteSection
                  key={writeAccount.id}
                  accountId={writeAccount.id}
                  canManageAds={writeAccount.canManage}
                  writeAllowed={writeAccount.writeAllowed}
                  lang={lang}
                />
              )}

              {error && (
                <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 12 }}>{error}</div>
              )}
            </>
          )}

          {!loading && error && !data && (
            <div className={EMPTY_STATE} style={{ color: '#b91c1c' }}>
              <p>{error}</p>
              {connection && (
                <div className="mt-3 flex flex-wrap justify-center gap-3">
                  {(connection.accounts?.length ?? 0) > 1 && (
                    <div className={ACCOUNT_SELECT_WRAP}>
                      <TaskySelect
                        aria-label={lang === 'en' ? 'Ad account' : '広告アカウント'}
                        value={selectedAccount?.id ?? ''}
                        onChange={(event) => selectAccount(Number(event.target.value))}
                      >
                        {connection.accounts?.map((account) => (
                          <option key={account.id} value={account.id}>
                            {reviewSafeText(account.account_name, lang, account.platform_account_id)}
                          </option>
                        ))}
                      </TaskySelect>
                    </div>
                  )}
                  <Button variant="outline" size="sm" onClick={loadData}>{t.refresh}</Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    </ResponsiveAppShell>
  );
};

export default MetaDashboardPage;
